import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useSubscription } from "@apollo/react-hooks";
import { Box } from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import {
  MY_LEAVE_ADD_LEAVE_TYPE_FILTER,
  MY_LEAVE_DELETE_LEAVE_TYPE_FILTER,
  MY_LEAVE_GET_FILTERED_TYPES,
  MY_LEAVE_LEAVES_SUBSCRIPTION,
} from "my-leave/graphql";
import {
  OcCard,
  OcCardButtonBar,
  OcCheckboxFormik,
  OcConfirmationDialog,
} from "oc/components";
import { useOcPolling, useOcSnackbar } from "oc/hooks";
import { useOcFormikHelper } from "oc/utils";

interface GraphQLDataItem {
  code: string;
  isSelectable: boolean;
  name: string;
  show: boolean;
  type: string;
  __typename: string;
}

export const MyLeaveAdminSettingsTypeFilterCard = () => {
  const { t } = useTranslation();
  const { setOpenMessage } = useOcSnackbar();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const showCompleteFeedback = () => {
    setOpenMessage({
      type: "success",
      message: t("MY_LEAVE_SUCCESSFULLY_SAVED"),
    });
  };

  const showErrorFeedback = (error: unknown) => {
    setOpenMessage({
      type: "error",
      message: t("OC_UNSUCCESSFULLY_SAVED"),
    });
    console.log(error);
  };

  const [
    deleteLeaveTypeFilter,
    { loading: addMutationLoading, error: addMutationError },
  ] = useMutation(MY_LEAVE_ADD_LEAVE_TYPE_FILTER, {
    onCompleted: showCompleteFeedback,
    onError: showErrorFeedback,
  });

  const [
    addLeaveTypeFilter,
    { loading: deleteMutationLoading, error: deleteMutationError },
  ] = useMutation(MY_LEAVE_DELETE_LEAVE_TYPE_FILTER, {
    onCompleted: showCompleteFeedback,
    onError: showErrorFeedback,
  });

  const {
    data: queryData,
    loading,
    error,
    refetch,
  } = useQuery(MY_LEAVE_GET_FILTERED_TYPES, {
    fetchPolicy: "cache-and-network",
    pollInterval: useOcPolling(1000 * 60),
  });

  useSubscription(MY_LEAVE_LEAVES_SUBSCRIPTION, {
    fetchPolicy: "network-only",
    onData: () => {
      refetch();
    },
  });

  const data: GraphQLDataItem[] = useMemo(() => {
    return queryData?.filteredLeaveTypes || [];
  }, [queryData]);

  const initialValues = useMemo(() => {
    let values: { [key: string]: boolean } = {};
    data.forEach((item) => {
      if (item.isSelectable) {
        values[item.code] = item.show;
      }
    });
    return values;
  }, [data]);

  const formik = useFormik({
    initialValues,
    onSubmit: (values) => {
      Object.keys(values).forEach((code) => {
        if (isFieldDirty(code)) {
          const show = values[code];
          if (show) {
            addLeaveTypeFilter({ variables: { code } });
          } else {
            deleteLeaveTypeFilter({ variables: { code } });
          }
        }
      });
      setShowConfirmDialog(false);
    },
    enableReinitialize: true,
  });

  const { isFieldDirty } = useOcFormikHelper({
    formik,
    defaultResource: "NOT_NEEDED", // TODO f
    valuesExtraData: {},
  });

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
    formik.resetForm();
  };

  const loadingState = loading || addMutationLoading || deleteMutationLoading;

  return (
    <Box>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={formik.submitForm}
        onCancel={handleDialogCancel}
        title={t("OC_CONFIRM_SETTING")}
        texts={[t("OC_CONFIRM_SETTING_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />

      <OcCard
        label={t("MY_LEAVE_GRANT_LEAVE_TYPES")}
        loading={loadingState}
        error={!!(error || addMutationError || deleteMutationError)}
      >
        <Box>
          <FormikProvider value={formik}>
            {data?.map((item: GraphQLDataItem) => {
              if (!item.isSelectable) return null;
              return (
                <Box key={`filter-${item.code}`}>
                  <OcCheckboxFormik
                    label={t(`MY_LEAVE_${item.code}`)}
                    name={item.code}
                    disabled={loadingState}
                    color="secondary"
                  />
                </Box>
              );
            })}
          </FormikProvider>
          <OcCardButtonBar
            leftButtonText={t("OC_REFRESH")}
            leftButtonColor="primary"
            leftButtonDisabled={loading}
            handleLeftButton={() => refetch()}
            handleRightButton={handleCancel}
            rightButtonText={t("OC_CANCEL")}
            rightButtonColor="secondary"
            handlePrimaryButton={() => setShowConfirmDialog(true)}
            primaryButtonText={t("OC_SAVE")}
            primaryButtonDisabled={!formik.dirty}
          />
        </Box>
      </OcCard>
    </Box>
  );
};
